import { useContext, useState } from 'react';
import styled from 'styled-components';
import {
  IconButton,
  IconLinkButton,
  ProcessingTooltip,
  Tooltip
} from '../atoms';
import type { TCourse } from '../../common/types/Course';
import { APIEndPoints, Request } from '../../apis';
import { z } from 'zod';
import { UserContext } from '../../contexts';

type TCoursesLink = {
  courseDetails: TCourse;
  subCourseId?: string;
  size: string;
};

type TProcessedMsg = string | null;
type TCourseLink = string | undefined;
type TEmailCourseLink = string | undefined;

type TExtraOptions = {
  subCourseId?: string;
};

const CourseLinksContainer = styled.span`
  display: flex;
  justify-content: flex-end;
`;

const CourseLinkWrapper = styled.div`
  position: relative;
`;

const ButtonWrapper = styled.span`
  @media (hover: hover) {
    &:hover {
      & + span {
        opacity: 1;
        visibility: visible;
      }
    }
  }
`;

const CoursesLink = ({ courseDetails, subCourseId, size }: TCoursesLink) => {
  const [processingTooltipOpen, setProcessingTooltipOpen] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [processedMsg, setProcessedMsg] = useState<TProcessedMsg>(null);
  const [courseLink, setCourseLink] = useState<TCourseLink>(undefined);
  const [emailCourseLink, setEmailCourseLink] =
    useState<TEmailCourseLink>(undefined);

  const { user, userInfo } = useContext(UserContext);
  const sub = user.sub;
  const userId = userInfo.userId;
  // TODO log
  // console.log('sub', sub);
  // console.log('userId', userId);
  const tokenSchema = z.string();

  const anytimeUrlBase = process.env.REACT_APP_ANYTIME_URL;

  const createLink = async (merchantApplying?: boolean) => {
    setCreatingLink(true);
    setProcessingTooltipOpen(true);

    if (courseDetails) {
      if (courseDetails.productType) {
        try {
          const extraOptions: TExtraOptions = {};
          if (subCourseId) {
            extraOptions.subCourseId = subCourseId;
          }
          const token = await Request.post(
            `${APIEndPoints.courseJWT}/?access_token`,
            tokenSchema,
            {
              courseId: courseDetails.id,
              merchantApplying: merchantApplying,
              productType: courseDetails.productType,
              merchantId: courseDetails.merchantId,
              ...extraOptions
            },
            {
              sub: userId,
              merchant: courseDetails.merchantId
            }
          );

          const courseUrl =
            anytimeUrlBase +
            '/customer-verification?jwt=' +
            token +
            '&productType=' +
            courseDetails.productType;

          if (merchantApplying) {
            setCreatingLink(false);
            setProcessingTooltipOpen(false);
            setCourseLink(courseUrl);
            setProcessedMsg('Link created');
            // open new tab, done like this to avoid typescript error
            const w = window.open(courseUrl, '_blank');
            if (w) {
              w.focus();
            }
          } else {
            setEmailCourseLink(courseUrl);
            return courseUrl;
          }
        } catch {
          catchHandling();
        }
      } else {
        catchHandling();
      }
    } else {
      catchHandling();
    }
  };

  const catchHandling = () => {
    setCreatingLink(false);
    setProcessingTooltipOpen(false);
    setProcessedMsg('Something went wrong');
  };

  //for email to students, merchantApplying is false
  const copyLink = async (url?: string) => {
    if (url) {
      setCreatingLink(true);
      setProcessingTooltipOpen(true);
      navigator.clipboard.writeText(url);
      setCreatingLink(false);
      setProcessedMsg('Copied to clipboard');
      setTimeout(() => {
        setProcessingTooltipOpen(false);
      }, 30);
    } else {
      const link = await createLink(false);
      if (link) {
        navigator.clipboard.writeText(link);
        setCreatingLink(false);
        setProcessingTooltipOpen(false);
        setProcessedMsg('Copied to clipboard');
      } else {
        catchHandling();
      }
    }
  };

  return (
    <CourseLinksContainer>
      <CourseLinkWrapper>
        {courseLink && (
          <>
            <ButtonWrapper>
              <IconLinkButton
                iconType="Apply"
                size={size}
                target="_blank"
                href={courseLink}
              />
            </ButtonWrapper>

            <Tooltip text="Apply" />
          </>
        )}
        {!courseLink && (
          <>
            <ButtonWrapper>
              <IconButton
                iconType="Apply"
                size={size}
                onClick={() => {
                  createLink(true); //for merchant in browser, merchantApplying is true
                }}
              />
            </ButtonWrapper>

            <Tooltip text="Apply" />
          </>
        )}
        <ButtonWrapper>
          <IconButton
            iconType="Email"
            size={size}
            onClick={() => {
              copyLink(emailCourseLink);
            }}
          />
        </ButtonWrapper>

        <Tooltip text="Copy to clipboard" />
        <ProcessingTooltip
          isTooltipVisible={processingTooltipOpen}
          processing={creatingLink}
          processedMsg={processedMsg}
        />
      </CourseLinkWrapper>
    </CourseLinksContainer>
  );
};

// Component doesn't require auth as it's called from auth'd pages
const SecureCoursesLink = CoursesLink;
export { SecureCoursesLink };
